<template>
  <VSection class="relative">
    <VContainer class="">
      <div class="bg-slate-50 dark:bg-slate-800 p-8 lg:px-15 lg:py-10 rounded-none border-l-8 border-primary shadow-sm">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <!-- Left side - Header and Description -->
          <div class="space-y-4">
            <h2 class="text-3xl font-bold text-slate-900 dark:text-slate-100">
              {{ t('newsletter.signup_title') }}
            </h2>
            <p class="text-lg leading-relaxed text-slate-600 dark:text-slate-300">
              {{ t('newsletter.signup_description') }}
            </p>
          </div>

          <!-- Right side - Form -->
          <div class="space-y-4">
            <form @submit.prevent="handleSubmit" class="space-y-4">
              <!-- Email Input with Button -->
              <div class="space-y-2">
                <Label for="footer-newsletter-email" class="sr-only">
                  {{ t('newsletter.email_label') }}
                </Label>
                <div class="flex gap-2">
                  <Input
                    id="footer-newsletter-email"
                    v-model="form.email"
                    type="email"
                    :placeholder="t('newsletter.email_placeholder')"
                    :class="{
                      'border-red-500': validationErrors.email,
                      'flex-1': true,
                    }"
                    :disabled="isSubmitting"
                    required
                  />
                  <VButton type="submit" variant="primary" size="default" :disabled="isSubmitting || !form.email || !form.newsletter_language || !form.acceptTerms" class="shrink-0">
                    <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
                    {{ isSubmitting ? t('newsletter.subscribing') : t('newsletter.subscribe_button') }}
                  </VButton>
                </div>
                <p v-if="validationErrors.email" class="text-sm text-red-600 dark:text-red-400">
                  {{ validationErrors.email }}
                </p>
              </div>

              <!-- Language Preference -->
              <div v-if="form.email && isValidEmail(form.email)" class="space-y-2">
                <Label class="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {{ t('newsletter.language_label') }} <span class="text-red-500">*</span>
                </Label>
                <Select v-model="form.newsletter_language" :disabled="isSubmitting">
                  <SelectTrigger
                    :class="{
                      'border-red-500': validationErrors.newsletter_language,
                    }"
                  >
                    <SelectValue :placeholder="t('newsletter.language_label')" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="it">
                      {{ t('newsletter.language_italian') }}
                    </SelectItem>
                    <SelectItem value="en">
                      {{ t('newsletter.language_english') }}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p v-if="validationErrors.newsletter_language" class="text-sm text-red-600 dark:text-red-400">
                  {{ validationErrors.newsletter_language }}
                </p>
              </div>

              <!-- Terms and Conditions Checkbox -->
              <div class="flex items-center space-x-3">
                <Checkbox
                  id="footer-newsletter-terms"
                  v-model:model-value="form.acceptTerms"
                  :class="{
                    'border-red-500': validationErrors.acceptTerms,
                  }"
                  :disabled="isSubmitting"
                />
                <div class="flex-1">
                  <Label
                    for="footer-newsletter-terms"
                    class="text-sm leading-relaxed cursor-pointer text-slate-700 dark:text-slate-300"
                  >
                    {{ t('newsletter.terms_label') }}
                  </Label>
                  <p v-if="validationErrors.acceptTerms" class="text-sm text-red-600 dark:text-red-400 mt-1">
                    {{ validationErrors.acceptTerms }}
                  </p>
                </div>
              </div>
            </form>

            <!-- Success Message -->
            <Alert
              v-if="showSuccess"
              class="border-green-300 bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-200"
            >
              <CheckCircle class="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle class="text-green-800 dark:text-green-200">{{ t('newsletter.success_title') }}</AlertTitle>
              <AlertDescription class="text-green-700 dark:text-green-300">
                {{ t('newsletter.success_message') }}
              </AlertDescription>
            </Alert>

            <!-- Error Message -->
            <Alert v-if="formError" class="border-red-300 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-200">
              <AlertCircle class="h-4 w-4 text-red-600 dark:text-red-400" />
              <AlertTitle class="text-red-800 dark:text-red-200">{{ t('newsletter.error_title') }}</AlertTitle>
              <AlertDescription class="text-red-700 dark:text-red-300">{{ formError }}</AlertDescription>
            </Alert>
          </div>
        </div>
      </div>
    </VContainer>
  </VSection>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-vue-next';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select';
import Alert from '~/components/ui/alert/Alert.vue';
import AlertDescription from '~/components/ui/alert/AlertDescription.vue';
import AlertTitle from '~/components/ui/alert/AlertTitle.vue';
import VButton from '~/components/VButton.vue';

// Interface for the form data
interface NewsletterFormData {
  email: string;
  acceptTerms: boolean;
  newsletter_language: string;
}

// Composables
const { t } = useI18n();
const { subscribe } = useNewsletter();

// State
const isSubmitting = ref(false);
const showSuccess = ref(false);
const formError = ref<string | null>(null);

// Form state
const form = reactive<NewsletterFormData>({
  email: '',
  acceptTerms: false,
  newsletter_language: '',
});

const validationErrors = reactive<Record<string, string>>({
  email: '',
  acceptTerms: '',
});

// Methods
const resetForm = () => {
  form.email = '';
  form.acceptTerms = false;
  form.newsletter_language = '';
  Object.keys(validationErrors).forEach((key) => {
    validationErrors[key] = '';
  });
  formError.value = null;
  showSuccess.value = false;
};

const validateForm = (): boolean => {
  let isValid = true;

  // Reset previous errors
  Object.keys(validationErrors).forEach((key) => {
    validationErrors[key] = '';
  });

  // Email validation
  if (!form.email.trim()) {
    validationErrors.email = t('newsletter.error_email_required');
    isValid = false;
  } else if (!isValidEmail(form.email)) {
    validationErrors.email = t('newsletter.error_email_invalid');
    isValid = false;
  }

  // Terms acceptance validation
  if (!form.acceptTerms) {
    validationErrors.acceptTerms = t('newsletter.error_terms_required');
    isValid = false;
  }

  // Language validation
  if (!form.newsletter_language) {
    validationErrors.newsletter_language = t('newsletter.error_language_required');
    isValid = false;
  }

  return isValid;
};

const isValidEmail = (email: string): boolean => {
  const tempInput = document.createElement('input');
  tempInput.type = 'email';
  tempInput.value = email;
  return tempInput.validity.valid && email.length > 0;
};

const handleSubmit = async () => {
  if (!validateForm()) {
    return;
  }

  isSubmitting.value = true;
  formError.value = null;

  try {
    // Use the newsletter SDK composable
    const response = await subscribe(form.email, form.newsletter_language);

    if (response.success) {
      // Show success message
      showSuccess.value = true;

      // Emit success event
      emit('subscribed', form.email);

      // Reset form after successful submission
      setTimeout(() => {
        resetForm();
      }, 3000);
    }
  } catch (error: any) {
    console.error('Newsletter subscription error:', error);

    // Handle different error scenarios
    let errorMessage = t('newsletter.error_subscription_failed');

    if (error.code === 400) {
      errorMessage = t('newsletter.error_email_invalid');
    } else if (error.message) {
      errorMessage = error.message;
    }

    formError.value = errorMessage;
    emit('error', errorMessage);
  } finally {
    isSubmitting.value = false;
  }
};

// Emit events for parent components if needed
const emit = defineEmits<{
  subscribed: [email: string];
  error: [error: string];
}>();

// Watch for successful subscription to emit event
watch(showSuccess, (newValue) => {
  if (newValue) {
    emit('subscribed', form.email);
  }
});

// Watch for errors to emit event
watch(formError, (newError) => {
  if (newError) {
    emit('error', newError);
  }
});
</script>
